import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Button, Chip, CircularProgress, Divider, Link, Stack, Typography, screenSizeBigger, StickyTooltip} from 'views/components';
import {Check} from 'views/icons';
import {CommandResponseView, ConnectionBadge, HoverToolBox, MarkdownViewer, Translate, translate, useCommand} from 'views/module';
import CommandTextViewer from '../../../../module/task/components/CommandTextViewer';

import {getDueDateColour, TASK_TYPE_CONFIG, predefinedReminderConfigs} from '../../utils';

import moment from 'moment';

const TaskItem = (props) => {
  const {
    task,
    onToggleTaskStatus,
    onClickEditTask,
    removeTask,
    playbooks,
    requester,
    skills,
    statusProgressWrap,
  } = props;
  const isStatusComplete = task.status === 'COMPLETE';
  const largerScreen = screenSizeBigger('sm');

  const dueDateMoment = moment(task.dueDate);
  const overdueDayCount = moment().startOf('day').diff(dueDateMoment.clone().startOf('day'), 'days');
  const maxDisplayedAvatarIcons = 5;
  const connections = task.connectionInfo || [];
  const ownerRns = Array.isArray(task.ownerRns) ? task.ownerRns :
    Array.isArray(task.ownerRn) ? task.ownerRn :
      task.ownerRn ? [task.ownerRn] : [];
  const owners = (task.ownerInfo || []).length > 0 ?
    task.ownerInfo :
    ownerRns
        .filter(Boolean)
        .map((ownerRn) => ({userRn: ownerRn, name: ownerRn}));
  const {clientConnections, nonClientConnections} = connections.reduce((acc, connection) => {
    const isClientConnection = /^RN:CLIENT-V1-[^:]+$/.test(connection.connectionRn);

    if (isClientConnection) {
      acc.clientConnections.push(connection);
    } else {
      acc.nonClientConnections.push(connection);
    }

    return acc;
  }, {clientConnections: [], nonClientConnections: []});
  const visibleClientConnection = clientConnections[0];
  const hiddenClientConnections = clientConnections.slice(1);
  const visibleNonClientConnections = nonClientConnections.slice(0, maxDisplayedAvatarIcons);
  const hiddenNonClientConnections = nonClientConnections.slice(maxDisplayedAvatarIcons);
  const description = task.description || {};
  const descriptionText = description.blob || translate('tasks.MyDay.TaskItem.Description.Empty');
  const isMarkdownDescription = description.editorType === 'MARKDOWN' && Boolean(description.blob);
  const playbook = playbooks.find((entry) => entry.playbookRn === task.playbookRn);

  const selectedReminderConfig = React.useMemo(() => {
    if (!task.reminderDate) {
      return null;
    }

    return predefinedReminderConfigs.find((config) => (
      task.reminderDate === config.converter(dueDateMoment)
    )) || null;
  }, [dueDateMoment, task.reminderDate]);

  const reminderDateLabel = React.useMemo(() => {
    if (moment(task.reminderDate).isBefore(moment())) {
      return '';
    }

    if (selectedReminderConfig?.code) {
      return selectedReminderConfig.code;
    }

    if (task.reminderDate) {
      const localReminderDate = moment(task.reminderDate).local();

      if (localReminderDate.isSame(moment(), 'day')) {
        return localReminderDate.format('h:mmA');
      } else {
        return localReminderDate.format('MMM D h:mmA');
      }
    }

    return '';
  }, [task.reminderDate, selectedReminderConfig]);

  const [toolbarVisible, setToolbarVisible] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const rightEdgeAnchorRef = React.useRef(null);

  const command = useCommand({
    requester,
    skills,
    statusProgressWrap,
    task,
  });

  const handleMouseEnter = () => {
    if (largerScreen) {
      setToolbarVisible(true);
      setAnchorEl(rightEdgeAnchorRef.current);
    }
  };

  const handleMouseLeave = () => {
    if (largerScreen) {
      setToolbarVisible(false);
      setAnchorEl(null);
    }
  };

  React.useEffect(() => {
    setToolbarVisible(false);
    setAnchorEl(null);
  }, [largerScreen]);

  const getConnectionDisplayName = (connection) => [connection.firstName, connection.lastName].filter(Boolean).join(' ');
  const nextTaskStatus = isStatusComplete ? 'OPEN' : 'COMPLETE';

  const handleApproveAndSendSuccess = () => {
    onToggleTaskStatus(task.taskRn, nextTaskStatus);
  };

  return (
    <Stack
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        position: 'relative',
        opacity: isStatusComplete ? 0.45 : 1,
      }}
    >
      <Stack
        spacing={1.25}
        sx={{
          py: 2,
          pr: 3,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
        >
          <Box
            onClick={() => onToggleTaskStatus(task.taskRn, nextTaskStatus)}
            sx={{
              'width': 24,
              'height': 24,
              'borderRadius': '50%',
              'border': '1.5px solid',
              'borderColor': isStatusComplete ? 'success.main' : 'divider',
              'backgroundColor': isStatusComplete ? 'success.main' : 'transparent',
              'display': 'flex',
              'alignItems': 'center',
              'justifyContent': 'center',
              'cursor': 'pointer',
              '& .todo-check': {
                transition: 'color 120ms ease',
                color: isStatusComplete ? 'common.white' : 'transparent',
                fontSize: 10,
              },
              '&:hover': {
                borderColor: isStatusComplete ? 'success.main' : 'success.main',
              },
              '&:hover .todo-check': {
                color: isStatusComplete ? 'common.white' : 'success.main',
              },
            }}
          >
            <Check className="todo-check" />
          </Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              flex: 1,
              minWidth: 0,
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                flexWrap: 'wrap',
                flex: 1,
                minWidth: 0,
              }}
            >
              {
                task?.type && (() => {
                  const type = task.type;
                  const typeConfig = TASK_TYPE_CONFIG[type] ?? TASK_TYPE_CONFIG.UNKNOWN;

                  return (
                    <Chip
                      key={type}
                      variant="outlined"
                      label={typeConfig.labelKey ? translate(typeConfig.labelKey) : type}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        ...typeConfig.chipSx,
                        borderWidth: 1,
                      }}
                    />
                  );
                })()
              }
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  textDecoration: isStatusComplete ? 'line-through' : 'none',
                  minWidth: 0,
                }}
              >
                <CommandTextViewer
                  value={task.title}
                  owners={owners}
                  connections={connections}
                  type={task.type}
                />
                {
                  command.canRunCommand &&
                    <Box
                      component='span'
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        ml: 1,
                      }}
                    >
                      <Box
                        component='span'
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 400,
                        }}
                      >
                        -
                      </Box>
                      {
                        command.isExecutingCommand &&
                          <CircularProgress
                            size={20}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#4B4843',
                              minWidth: '20px',
                              marginLeft: '12px',
                            }}
                          />
                      }
                      {
                        !command.isExecutingCommand &&
                          <Button
                            variant='outlined'
                            size='small'
                            onClick={command.runCommand}
                            sx={{
                              minWidth: '30px',
                              px: '12px',
                              py: '8px',
                              borderRadius: '7px',
                              borderColor: '#D8D4CE',
                              background: '#FCFCFC',
                              color: '#4B4843',
                              fontSize: '11px',
                              fontWeight: 500,
                              lineHeight: '100%',
                              textTransform: 'none',
                            }}
                          >
                            <Translate expression='tasks.Common.Run' />
                          </Button>
                      }
                    </Box>
                }
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        <Stack
          spacing={1}
          sx={{
            pl: '40px',
          }}
        >
          {
            isMarkdownDescription &&
              <Box
                sx={{
                  'color': 'text.secondary',
                  '& .wmde-markdown': {
                    backgroundColor: 'transparent',
                    color: 'inherit',
                  },
                  '& p': {
                    m: 0,
                  },
                  '& *': {
                    fontSize: '0.875rem',
                    lineHeight: 1.43,
                  },
                }}
              >
                <MarkdownViewer value={description.blob} />
              </Box>
          }
          {
            !isMarkdownDescription &&
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                <CommandTextViewer
                  value={descriptionText}
                  owners={owners}
                  connections={connections}
                  type={task.type}
                />
              </Typography>
          }
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              flexWrap: 'wrap',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: getDueDateColour(isStatusComplete, dueDateMoment),
              }}
            >
              {
                overdueDayCount === 1 && (
                  <Translate
                    expression="tasks.MyDay.TaskItem.Due.YesterdayLabel"
                    substitute={{time: dueDateMoment.format('h:mmA')}}
                  />
                )
              }

              {
                overdueDayCount > 1 &&
                    <Translate
                      expression="tasks.MyDay.TaskItem.Due.DaysAgoLabel"
                      substitute={{
                        days: overdueDayCount,
                        time: dueDateMoment.format('h:mmA'),
                      }}
                    />
              }
              {
                overdueDayCount < 1 &&
                    <Translate
                      expression="tasks.MyDay.TaskItem.Due.Label"
                      substitute={{time: dueDateMoment.format('h:mmA')}}
                    />
              }
            </Typography>
            {
              reminderDateLabel &&
              <>
                <Box
                  sx={{
                    height: 4,
                    width: 4,
                    borderRadius: '50%',
                    backgroundColor: 'text.secondary',
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: '#C768C7',
                  }}
                >
                  {reminderDateLabel}
                </Typography>
              </>
            }
            {
              visibleClientConnection &&
              <>
                <Box
                  sx={{
                    height: 4,
                    width: 4,
                    borderRadius: '50%',
                    backgroundColor: 'text.secondary',
                  }}
                />
                <Stack
                  direction='row'
                  spacing={0.5}
                  alignItems='center'
                  sx={{
                    minWidth: 0,
                  }}
                >
                  <Link
                    href={`/client/${visibleClientConnection.connectionRn}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    underline='none'
                    sx={{
                      'fontSize': '0.75rem',
                      'lineHeight': 1.66,
                      'fontWeight': 400,
                      'color': 'text.secondary',
                      'cursor': 'pointer',
                      '&:hover': {
                        color: 'primary.main',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {getConnectionDisplayName(visibleClientConnection)}
                  </Link>
                  {
                    hiddenClientConnections.length > 0 &&
                      <StickyTooltip
                        title={
                          <Stack spacing={0.5}>
                            {
                              hiddenClientConnections.map((clientConnection) => (
                                <Link
                                  key={clientConnection.connectionRn}
                                  href={`/client/${clientConnection.connectionRn}`}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  sx={{
                                    'color': 'inherit',
                                    'fontSize': '12px',
                                    'lineHeight': 1.4,
                                    'textDecoration': 'none',
                                    '&:hover': {
                                      color: 'primary.light',
                                      textDecoration: 'underline',
                                    },
                                  }}
                                >
                                  {getConnectionDisplayName(clientConnection)}
                                </Link>
                              ))
                            }
                          </Stack>
                        }
                      >
                        <Typography
                          variant='caption'
                          sx={{
                            'fontWeight': 400,
                            'color': 'text.secondary',
                            'whiteSpace': 'nowrap',
                            'textDecoration': 'none',
                            'cursor': 'pointer',
                          }}
                        >
                          <Translate
                            expression='tasks.MyDay.TaskItem.MoreClients'
                            substitute={{count: hiddenClientConnections.length}}
                          />
                        </Typography>
                      </StickyTooltip>
                  }
                </Stack>
              </>
            }
            {
              nonClientConnections.length > 0 &&
              <>
                <Box
                  sx={{
                    height: 4,
                    width: 4,
                    borderRadius: '50%',
                    backgroundColor: 'text.secondary',
                  }}
                />
                <Stack
                  direction="row"
                  spacing={-.5}
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  {
                    visibleNonClientConnections.map((connection, index) => (
                      <ConnectionBadge
                        key={connection.connectionRn}
                        connection={connection}
                        sx={{
                          width: '28px',
                          height: '28px',
                          position: 'relative',
                          zIndex: index + 1,
                          fontSize: '12px',
                          outline: '2px solid',
                          outlineColor: 'background.paper',
                        }}
                      />
                    ))
                  }
                  {
                    hiddenNonClientConnections.length > 0 &&
                      <StickyTooltip
                        title={hiddenNonClientConnections.map((connection) => getConnectionDisplayName(connection)).join(', ')}
                      >
                        <Box
                          sx={{
                            width: '28px',
                            height: '28px',
                            position: 'relative',
                            zIndex: visibleNonClientConnections.length + 1,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#E2DEDA',
                            color: 'text.secondary',
                            fontSize: '11px',
                            fontWeight: 600,
                            outline: '2px solid',
                            outlineColor: 'background.paper',
                          }}
                        >
                          +{hiddenNonClientConnections.length}
                        </Box>
                      </StickyTooltip>
                  }
                </Stack>
              </>
            }
            {
              Boolean(playbook) &&
                <>
                  <Box
                    sx={{
                      height: 4,
                      width: 4,
                      borderRadius: '50%',
                      backgroundColor: 'text.secondary',
                    }}
                  />
                  <Typography
                    variant='caption'
                    component='button'
                    onClick={() => window.open(`/playbooks?selectedPlaybookRn=${encodeURIComponent(playbook.playbookRn)}`)}
                    sx={{
                      'background': 'none',
                      'border': 'none',
                      'color': '#DA7955',
                      'cursor': 'pointer',
                      'fontSize': '12px',
                      'p': 0,
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    /{playbook.title}
                  </Typography>
                </>
            }
          </Stack>
          {
            command.response &&
              <CommandResponseView
                communicationTarget={command.communicationTarget}
                requester={requester}
                response={command.response}
                onApproveAndSendSuccess={handleApproveAndSendSuccess}
                onDiscard={command.discard}
                isRegenerating={command.isExecutingCommand}
                onRegenerate={command.regenerate}
              />
          }
        </Stack>
      </Stack>
      <Box
        ref={rightEdgeAnchorRef}
        sx={{
          position: 'absolute',
          right: -100,
          top: 20,
          width: 0,
          height: 0,
        }}
      />
      <HoverToolBox
        toolBar={toolbarVisible}
        anchorEl={anchorEl}
        onClickEdit={() => onClickEditTask(task)}
        onClickDelete={() => removeTask(task)}
        hideTool={{
          emoji: true,
          copy: true,
          edit: false,
          delete: false,
          reply: true,
        }}
      />
      <Divider />
    </Stack>
  );
};

TaskItem.propTypes = {
  task: PropTypes.object.isRequired,
  onToggleTaskStatus: PropTypes.func.isRequired,
  onClickEditTask: PropTypes.func,
  removeTask: PropTypes.func,
  playbooks: PropTypes.array.isRequired,
  requester: PropTypes.object.isRequired,
  skills: PropTypes.array.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  playbooks: state.playbooks.allPlaybooks,
  requester: state.user,
  skills: state.skills.allSkills,
});

export default connect(mapStateToProps, dispatch)(React.memo(TaskItem));
